import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators, FormControl } from '@angular/forms';
import type { User } from '../../../shared/models/interfaces/user.interface';
import { ButtonComponent } from "../../../shared/components/button/button.component";
import { ButtonTypeEnum } from '../../../shared/components/button/types/button-type.enum';
import { ButtonRadiusEnum } from '../../../shared/components/button/types/button-radius.enum';
import { NgxMaskDirective } from 'ngx-mask';
import { FloatLabelModule } from 'primeng/floatlabel';
import { InputTextModule } from 'primeng/inputtext';
import { UserForm } from '../../../shared/models/interfaces/forms/user-form.interface';
import { ToastService } from '../../../shared/components/toast/service/toast.service';
import { MeService } from '../../../shared/services/me/me.service';
import { UploadService } from '../../../shared/services/upload/upload.service';

@Component({
  selector: 'l-profile-information',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ButtonComponent,
    FloatLabelModule,
    InputTextModule,
    NgxMaskDirective,
  ],
  templateUrl: './profile-information.component.html'
})
export class ProfileInformationComponent implements OnInit {
  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly toastService: ToastService,
    private readonly meService: MeService,
    private readonly uploadService: UploadService,
  ) {
    this.formGroup = this.formBuilder.group<UserForm>({
      name: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
      email: new FormControl('', { nonNullable: true, validators: [Validators.required, Validators.email] }),
      birthDate: new FormControl('', { nonNullable: true }),
      phone: new FormControl('', { nonNullable: true }),
      cpfCnpj: new FormControl('', { nonNullable: true }),
      institution: new FormControl('', { nonNullable: true }),
      imageUrl: new FormControl(null, [])
    });
  }

  @Input() public user: User | null = null;

  @Input() public avatarFile: File | null = null;

  @Output() public editingChange = new EventEmitter<boolean>();

  @Output() public userChange = new EventEmitter<User>();

  public formGroup!: FormGroup<UserForm>;

  public isEditing: boolean = false;

  public isSaving: boolean = false;

  public buttonTypeEnum: typeof ButtonTypeEnum = ButtonTypeEnum;

  public buttonRadiusEnum: typeof ButtonRadiusEnum = ButtonRadiusEnum;

  public readonly phoneMask: string = '(00) 00000-0000';

  public readonly dateMask: string = '00/00/0000';

  public readonly cpfCnpjMask: string = '000.000.000-00';

  public ngOnInit(): void {
    this.initializeForm();
  }

  public cancelEdit(): void {
    if (!this.isEditing || this.isSaving) {
      return;
    }

    this.toggleEditMode();
  }

  public async saveInformation(): Promise<void> {
    if (this.isSaving) {
      return;
    }

    if (this.formGroup.invalid) {
      this.toastService.error('Por favor, preencha os campos obrigatórios corretamente.', 'Oops...');
      return;
    }

    this.isSaving = true;
    const payload = this.formGroup.getRawValue();

    try {
      if (this.avatarFile) {
        try {
          payload.imageUrl = await this.uploadService.uploadPublicImage(this.avatarFile);
        } catch {
          this.toastService.error('Não foi possível salvar a foto.', 'Oops...');
          return;
        }
      }

      const updatedUser = await this.meService.updateProfile(payload);

      if (!updatedUser) {
        this.toastService.error('Não foi possível salvar as informações do perfil.', 'Oops...');
        return;
      }

      this.user = updatedUser;
      this.userChange.emit(updatedUser);
      this.toastService.success('Perfil atualizado com sucesso.');
      this.toggleEditMode();
    } catch {
      this.toastService.error('Não foi possível salvar as informações do perfil.', 'Oops...');
    } finally {
      this.isSaving = false;
    }
  }

  public toggleEditMode(): void {
    this.isEditing = !this.isEditing;
    this.editingChange.emit(this.isEditing);

    if (this.isEditing) {
      Object.keys(this.formGroup.controls).forEach(key => {
        if (key === 'email') {
          this.formGroup.get(key)?.disable();
          return;
        }

        this.formGroup.get(key)?.enable();
      });
    } else {
      Object.keys(this.formGroup.controls).forEach(key => {
        this.formGroup.get(key)?.disable();
      });
      this.formGroup.reset(this.getFormResetValues(this.user));
    }
  }

  private initializeForm(): void {
    this.formGroup.reset(this.getFormResetValues(this.user));

    Object.keys(this.formGroup.controls).forEach(key => {
      this.formGroup.get(key)?.disable();
    });
  }

  private getFormResetValues(user: User | null): {
    name: string;
    email: string;
    birthDate: string;
    phone: string;
    cpfCnpj: string;
    institution: string;
    imageUrl: string | null;
  } {
    return {
      name: user?.name ?? '',
      email: user?.email ?? '',
      birthDate: user?.birthDate ?? '',
      phone: user?.phone ?? '',
      cpfCnpj: user?.cpfCnpj ?? '',
      institution: user?.institution ?? '',
      imageUrl: user?.imageUrl ?? null,
    };
  }
}
